import { FC } from "react";
import { Route, Routes } from "react-router";

import Roles from "../../models/roles";

import PrivateRoute from "../../components/UI/Routes/PrivateRoute";
import PublicRoute from "../../components/UI/Routes/PublicRoute";

/** Layouts */
import AuthLayout from "../../layouts/Authentication";
import EnquiryLayout from "../../layouts/Enquiry";
import OnboardingStep from "../../layouts/Onboarding";
import Portal from "../../layouts/Portal";

/** Auth Components */
import EmailVerification from "../EmailVerification";
import ForgotPassword from "../ForgotPassword";
import Login from "../Login";
import ResetPassword from "../ResetPassword";
import SentInvitation from "../SentInvitation";
import Signup from "../Signup";

/** Onboarding Steps Components */
import IdVerfication from "../../components/OnboardingSteps/IdVerification";
import JobPreferences from "../../components/OnboardingSteps/JobPreferences";
import OnlineInterview from "../../components/OnboardingSteps/OnlineInterview";
import PersonalDetails from "../../components/OnboardingSteps/PersonalDetails";
import Qualifications from "../../components/OnboardingSteps/Qualifications";

/** Candidate Components */
import Centres from "../../components/Candidate/Centres";
import AcceptedListing from "../../components/Candidate/Jobs/AcceptedListing";
import CalendarView from "../../components/Candidate/Jobs/Calendar";
import HistoryListing from "../../components/Candidate/Jobs/HistoryListing";
import Jobs from "../../components/Candidate/Jobs/JobListings";
import PendingListing from "../../components/Candidate/Jobs/PendingListing";
import MyProfileWrapper from "../../components/Candidate/MyProfileWrapper";
import Settings from "../../components/Candidate/Settings";
import Timesheet from "../../components/Candidate/Timesheet";
import History from "../../components/Candidate/Timesheet/History";

/* My profile Components */
import BankAccount from "../../components/OnboardingSteps/BankAccount";
import CasualAndFairWorkStatement from "../../components/OnboardingSteps/CasualAndFairWorkStatement";
import ComplainceCheck from "../../components/OnboardingSteps/ComplainceCheck";
import ContractForm from "../../components/OnboardingSteps/ContractForm";
import NextOfKin from "../../components/OnboardingSteps/NextOfKin";
import Superannuation from "../../components/OnboardingSteps/Superannuation";
import TfnForm from "../../components/OnboardingSteps/TfnForms";

/* Enquiry Components */
import ClientCenter from "../../components/Client/CentresAndUsers/Centres";
import CentreEnquiry from "../../components/Client/CentresAndUsers/Enquiry";
import CenterUsers from "../../components/Client/CentresAndUsers/Users";
import VacanciesHistory from "../../components/Client/Vacancies/history";
import ViewVacancy from "../../components/Client/Vacancies/ViewVacancy";
import Enquiry from "../../components/Enquiry";
import SuccessEnquiry from "../../components/Enquiry/SuccessEnquiry";

/** Super Admin Components */
import AdminCandidates from "../../components/SuperAdmin/Candidates";
import AdminCentres from "../../components/SuperAdmin/CentresAndUsers/Centres";
import Dashboard from "../../components/SuperAdmin/Dashboard";
import AdminJobs from "../../components/SuperAdmin/Jobs/UrgentVacancy";
import CandidateProfileEdit from "../../components/SuperAdmin/MyProfile";
import ReferralTracker from "../../components/SuperAdmin/ReferralTracker";
import AdminSettings from "../../components/SuperAdmin/Settings";
import AdminTimesheet from "../../components/SuperAdmin/Timesheet";
import TimesheetApproval from "../../components/SuperAdmin/Timesheet/approval";
import AdminTimesheetHistory from "../../components/SuperAdmin/Timesheet/history";
import User from "../../components/SuperAdmin/User";

/* General */
import Candidates from "../../components/Client/Candidates";
import BlockedCandidates from "../../components/Client/Candidates/BlockedCandidates";
import ClientSettings from "../../components/Client/Settings";
import ClientTimesheet from "../../components/Client/Timesheet";
import Vacancies from "../../components/Client/Vacancies";
import AdminUrgentVacancies from "../../components/SuperAdmin/Jobs/UrgentVacancy";
import TransparentPreloader from "../../components/UI/TransparentLoader";
import PrivacyPolicy from "../../components/WelcomeContent/PrivacyPolicy";

import CreateCentres from "../../components/Centres";
import SetupNewCentres from "../../components/Centres/SetupNewCentres";
import ErrorRedirect from "../../components/ConnectID/ErrorRedirect";
import CompletedProfile from "../../components/OnboardingSteps/CompletedProfile";
import CentreUsers from "../../components/SuperAdmin/CentresAndUsers/Users";
import AcceptedVacancy from "../../components/SuperAdmin/Jobs/AcceptedVacancy";
import HistoryVacancy from "../../components/SuperAdmin/Jobs/HistoryVacancy/history";
import ScheduledVacancy from "../../components/SuperAdmin/Jobs/ScheduledVacancy/scheduled";
import SaveNotification from "../../components/UI/SaveNotification";
import CreateVacancy from "../../components/Vacancy";

import ComplianceCheckEditPage from "../../components/SuperAdmin/Candidates/ComplianceCheckEdit";
import JobAdderId from "../../components/SuperAdmin/Candidates/JobAdderId";
import PayrollPending from "../../components/SuperAdmin/Timesheet/PayrollPending";
import TermsOfUse from "../../components/WelcomeContent/TermsOfUse";
import EmailVerified from "../EmailVerification/EmailVerified";
import NotFoundLoggedIn from "../ErrorPage/NotFoundLoggedIn";
import NotFoundLoggedOut from "../ErrorPage/NotFoundLoggedOut";
import ImpersonationEntry from "../Impersonation";
import ActivatedPage from "../ResetPassword/ActivatedPage";

interface RouterProps {}

const isAuthenticated = (): boolean => {
  return !!localStorage.getItem("token"); // or your actual auth logic
};

export enum NON_AUTH_ROUTE {
  LOGIN = "/",
  SIGNUP = "/signup",
  FORGOT_PASSWORD = "/forgot-password",
  SENT_INVITATION = "/sent-invitation",
  RESET_PASSWORD = "/reset-password",
  SET_PASSWORD = "/set-password",
  EMAIL_VERIFICATION = "/email-verification",
  CREATE_ENQUIRY = "/create-enquiry",
  SUCCESS_ENQUIRY = "/enquiry/success",
  PRIVACY_POLICY = "/privacy-policy",
  TERMS_OF_SERVICE = "/terms-of-use",
  PRIVACY_POLICY_ENQUIRY = "/en/privacy-policy",
  TERMS_OF_SERVICE_ENQUIRY = "/en/terms-of-use",
  ACCOUNT_ACTIVATION = "/account-activation",
  IMPERSONATION_ENTRY = "/impersonate",
  EMAIL_VERIFIED = "/email-verified",
}

export enum AUTH_ROUTE {
  // Onboarding Steps
  ID_VERIFICATION = "/onboarding/id-verfication", //step 1
  PERSONAL_DETAILS = "/onboarding/personal-details", //step 2
  QUALIFICATIONS = "/onboarding/qualifications", //step 3
  JOB_PREFERENCES = "/onboarding/job-preferences", //step 4
  ONLINE_INTERVIEW = "/onboarding/online-interview", //step 5

  //Candidate
  JOBS = "/candidate/jobs/listings",
  PENDING_JOBS = "/candidate/jobs/pending",
  ACCEPTED_JOBS = "/candidate/jobs/accepted",
  HISTORY_JOBS = "/candidate/jobs/history",
  JOBS_CALENDAR = "/candidate/jobs/calendar",
  JOBS_AVAILABILITIES = "/candidate/jobs/availabilities",
  CENTRES = "/candidate/centres",
  MY_PROFILE = "/candidate/my-profile",
  TIMESHEET = "/candidate/timesheet",
  TIMESHEET_HISTORY = "/candidate/timesheet/history",
  SETTINGS = "/candidate/settings",
  MYPROFILE_COMPLETED = "/candidate/my-profile/completed",

  //Super-Admin
  ADMIN_DASHBOARD = "/admin/dashboard",
  ADMIN_JOBS = "/admin/jobs",
  ADMIN_JOBS_SCHEDULED = "/admin/jobs/scheduled",
  ADMIN_PENDING_VACANCIES = "/admin/vacancies/vacancy-details",
  ADMIN_URGENT_VACANCIES = "/admin/vacancies/urgent",
  ADMIN_SCHEDULED_VACANCIES = "/admin/vacancies/scheduled",
  ADMIN_ACCEPTED_VACANCIES = "/admin/vacancies/accepted",
  ADMIN_HISTORY_VACANCIES = "/admin/vacancies/history",
  ADMIN_CREATE_VACANCY = "/admin/create-vacancy",
  ADMIN_EDIT_VACANCY = "/admin/edit-vacancy",
  ADMIN_CENTERS = "/admin/centres",
  ADMIN_SETUP_NEW_CENTRE = "/admin/centres/setup",
  ADMIN_CENTERS_USERS = "/admin/centres/users",
  ADMIN_CANDIDATES = "/admin/employees",
  ADMIN_TIMESHEET_PENDING = "/admin/timesheet/pending",
  ADMIN_TIMESHEET_APPROVAL = "/admin/timesheet/approval",
  ADMIN_TIMESHEET_HISTORY = "/admin/timesheet/history",
  ADMIN_TIMESHEET_PAYROLL_PENDING = "/admin/timesheet/payroll-pending",
  ADMIN_REFERRAL = "/admin/referral-tracker",
  ADMIN_USERS = "/admin/users",
  ADMIN_SETTINGS = "/admin/settings",
  ADMIN_CREATE_CENTRE = "/admin/centre/create",
  ADMIN_EDIT_CENTRE = "/admin/centre/edit",
  ADMIN_CANDIDATE_PROFILE = "/admin/candidate-profile",
  ADMIN_CANDIDATE_PROFILE_EDIT = "/admin/candidate-profile/id-verification",
  ADMIN_CANDIDATE_PROFILE_EDIT_REQUEST = "/admin/candidate-profile/personal-details",
  ADMIN_CANDIDATE_COMPLIANCE_CHECK_EDIT = "/admin/candidate-profile/complaince-check/edit",

  //Centre-Admin
  CLIENT_VACANCIES = "/client/vacancies",
  VACANCY_HISTORY = "/client/vacancies/history",
  CREATE_VACANCY = "/client/create-vacancy",
  EDIT_VACANCY = "/client/edit-vacancy",
  PENDING_VACANCY = "/client/vacancies/vacancy-details",
  CLIENT_CANDIDATES = "/client/candidates",
  CLIENT_CANDIDATES_BLOCKED = "/client/candidates/blocked",
  CLIENT_CENTRES = "/client/centres",
  CLIENT_TIMESHEET_PENDING = "/client/timesheet/pending",
  CLIENT_TIMESHEET_APPROVAL = "/client/timesheet/approval",
  CLIENT_TIMESHEET_HISTORY = "/client/timesheet/history",
  CLIENT_SETTINGS = "/client/settings",
  CLIENT_EDIT_CENTRE = "/client/centre/edit",
  CLIENT_USERS = "/client/centres/users",
  CLIENT_ENQUIRY = "/client/centres/enquiry",
}

const Router: FC<RouterProps> = () => {
  return (
    <>
      <TransparentPreloader />
      <div className="app-container">
        <ErrorRedirect />
        <Routes>
          {/* Unauthenticated routes */}
          <Route
            path={NON_AUTH_ROUTE.LOGIN}
            element={
              <PublicRoute layout={AuthLayout} component={Login} exact />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.SIGNUP}
            element={<PublicRoute layout={AuthLayout} component={Signup} />}
          />
          <Route
            path={NON_AUTH_ROUTE.FORGOT_PASSWORD}
            element={
              <PublicRoute layout={AuthLayout} component={ForgotPassword} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.SENT_INVITATION}
            element={
              <PublicRoute layout={AuthLayout} component={SentInvitation} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.EMAIL_VERIFIED}
            element={
              <PublicRoute layout={AuthLayout} component={EmailVerified} />
            }
          />  
          <Route
            path={NON_AUTH_ROUTE.RESET_PASSWORD}
            element={
              <PublicRoute layout={AuthLayout} component={ResetPassword} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.SET_PASSWORD}
            element={
              <PublicRoute layout={AuthLayout} component={ResetPassword} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.EMAIL_VERIFICATION}
            element={
              <PublicRoute layout={AuthLayout} component={EmailVerification} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.CREATE_ENQUIRY}
            element={<PublicRoute layout={EnquiryLayout} component={Enquiry} />}
          />
          <Route
            path={NON_AUTH_ROUTE.SUCCESS_ENQUIRY}
            element={
              <PublicRoute layout={EnquiryLayout} component={SuccessEnquiry} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.PRIVACY_POLICY}
            element={
              <PublicRoute layout={AuthLayout} component={PrivacyPolicy} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.TERMS_OF_SERVICE}
            element={<PublicRoute layout={AuthLayout} component={TermsOfUse} />}
          />
          <Route
            path={NON_AUTH_ROUTE.PRIVACY_POLICY_ENQUIRY}
            element={
              <PublicRoute layout={EnquiryLayout} component={PrivacyPolicy} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.TERMS_OF_SERVICE_ENQUIRY}
            element={
              <PublicRoute layout={EnquiryLayout} component={TermsOfUse} />
            }
          />
          <Route
            path={NON_AUTH_ROUTE.ACCOUNT_ACTIVATION}
            element={
              <PublicRoute layout={AuthLayout} component={ActivatedPage} />
            }
          />
          {/* Impersonation Entry Route - for admin access to candidate profiles */}
          <Route
            path={NON_AUTH_ROUTE.IMPERSONATION_ENTRY}
            element={<ImpersonationEntry />}
          />
          {/* Authenticated routes */}

          {/* Start - Onboarding Routes */}
          <Route
            path={AUTH_ROUTE.ID_VERIFICATION}
            element={
              <PrivateRoute
                layout={OnboardingStep}
                component={IdVerfication}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.PERSONAL_DETAILS}
            element={
              <PrivateRoute
                layout={OnboardingStep}
                component={PersonalDetails}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.QUALIFICATIONS}
            element={
              <PrivateRoute
                layout={OnboardingStep}
                component={Qualifications}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.JOB_PREFERENCES}
            element={
              <PrivateRoute
                layout={OnboardingStep}
                component={JobPreferences}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ONLINE_INTERVIEW}
            element={
              <PrivateRoute
                layout={OnboardingStep}
                component={OnlineInterview}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          {/* End - Onboarding Routes */}

          {/* Start - Candidates Routes */}
          <Route
            path={AUTH_ROUTE.JOBS}
            element={
              // <CandidateVerifiedRoute>
                <PrivateRoute
                  layout={Portal}
                  component={Jobs}
                  roles={[Roles.CANDIDATE]}
                />
              // </CandidateVerifiedRoute>
            }
          />
          <Route
            path={AUTH_ROUTE.PENDING_JOBS}
            element={
              <PrivateRoute
                layout={Portal}
                component={PendingListing}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ACCEPTED_JOBS}
            element={
              <PrivateRoute
                layout={Portal}
                component={AcceptedListing}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.HISTORY_JOBS}
            element={
              <PrivateRoute
                layout={Portal}
                component={HistoryListing}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.JOBS_CALENDAR}
            element={
              <PrivateRoute
                layout={Portal}
                component={CalendarView}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.JOBS_CALENDAR}
            element={
              <PrivateRoute
                layout={Portal}
                component={CalendarView}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          {/* <Route
          path={AUTH_ROUTE.JOBS_AVAILABILITIES}
          element={
            <PrivateRoute
              layout={Portal}
              component={JobsAvailabilities}
              roles={[Roles.CANDIDATE]}
            />
          }
        /> */}
          <Route
            path={AUTH_ROUTE.CENTRES}
            element={
              // <CandidateVerifiedRoute>
                <PrivateRoute
                  layout={Portal}
                  component={Centres}
                  roles={[Roles.CANDIDATE, Roles.CENTER_ADMIN]}
                />
              // </CandidateVerifiedRoute>
            }
          />
          <Route
            path={AUTH_ROUTE.MY_PROFILE}
            element={
              <PrivateRoute
                layout={Portal}
                component={MyProfileWrapper}
                roles={[Roles.CANDIDATE]}
              />
            }
          >
            {/* Start - Stage 1 */}
            <Route path="id-verification" element={<IdVerfication />} />
            <Route path="personal-details" element={<PersonalDetails />} />
            <Route path="qualifications" element={<Qualifications />} />
            <Route path="job-preferences" element={<JobPreferences />} />
            {/* <Route path="availabilities" element={<Availabilities />} /> */}
            <Route path="online-interview" element={<OnlineInterview />} />
            {/* End - Stage 1 */}
            {/* Start - Stage 2 */}
            <Route path="complaince-check" element={<ComplainceCheck />} />
            {/* End - Stage 2 */}
            {/* Start - Stage 3 */}
            <Route path="tfn-declaration" element={<TfnForm />} />
            <Route path="superannuation" element={<Superannuation />} />
            <Route path="contract-form" element={<ContractForm />} />
            <Route path="casual-and-fair-work-statement" element={<CasualAndFairWorkStatement />} />
            <Route path="bank-account" element={<BankAccount />} />
            <Route path="next-of-kin" element={<NextOfKin />} />
            {/* End - Stage 3 */}
          </Route>
          <Route
            path={AUTH_ROUTE.MYPROFILE_COMPLETED}
            element={
              <PrivateRoute
                layout={Portal}
                component={CompletedProfile}
                roles={[Roles.CANDIDATE]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.TIMESHEET}
            element={
              // <CandidateVerifiedRoute>
                <PrivateRoute
                  layout={Portal}
                  component={Timesheet}
                  roles={[Roles.CANDIDATE]}
                />
              // </CandidateVerifiedRoute>
            }
          />
          <Route
            path={AUTH_ROUTE.TIMESHEET_HISTORY}
            element={
              // <CandidateVerifiedRoute>
                <PrivateRoute
                  layout={Portal}
                  component={History}
                  roles={[Roles.CANDIDATE]}
                />
              // </CandidateVerifiedRoute>
            }
          />
          <Route
            path={AUTH_ROUTE.SETTINGS}
            element={
              // <CandidateVerifiedRoute>
                <PrivateRoute
                  layout={Portal}
                  component={Settings}
                  roles={[Roles.CANDIDATE]}
                />
              // </CandidateVerifiedRoute>
            }
          />
          {/* End - Candidates Routes */}

          {/* Start - Admin Routes */}
          <Route
            path={AUTH_ROUTE.ADMIN_DASHBOARD}
            element={
              <PrivateRoute
                layout={Portal}
                component={Dashboard}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_JOBS}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminJobs}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_JOBS_SCHEDULED}
            element={
              <PrivateRoute
                layout={Portal}
                component={ScheduledVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_JOBS_SCHEDULED}
            element={
              <PrivateRoute
                layout={Portal}
                component={ScheduledVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_URGENT_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminUrgentVacancies}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_SCHEDULED_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={ScheduledVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_ACCEPTED_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={AcceptedVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_HISTORY_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={HistoryVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_CREATE_VACANCY}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_EDIT_VACANCY}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_CANDIDATES}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminCandidates}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_CENTERS}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminCentres}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_SETUP_NEW_CENTRE}
            element={
              <PrivateRoute
                layout={Portal}
                component={SetupNewCentres}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_CENTERS_USERS}
            element={
              <PrivateRoute
                layout={Portal}
                component={CentreUsers}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_TIMESHEET_PENDING}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminTimesheet}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_TIMESHEET_APPROVAL}
            element={
              <PrivateRoute
                layout={Portal}
                component={TimesheetApproval}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_TIMESHEET_PAYROLL_PENDING}
            element={
              <PrivateRoute
                layout={Portal}
                component={PayrollPending}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_TIMESHEET_HISTORY}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminTimesheetHistory}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_REFERRAL}
            element={
              <PrivateRoute
                layout={Portal}
                component={ReferralTracker}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_USERS}
            element={
              <PrivateRoute
                layout={Portal}
                component={User}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_SETTINGS}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminSettings}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_CREATE_CENTRE}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateCentres}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.ADMIN_EDIT_CENTRE}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateCentres}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />

          <Route
            path={AUTH_ROUTE.ADMIN_CANDIDATE_PROFILE}
            element={
              <PrivateRoute
                layout={Portal}
                component={CandidateProfileEdit}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          >
            {/* Start - Stage 1 */}
            <Route path="id-verification" element={<IdVerfication />} />
            <Route path="personal-details" element={<PersonalDetails />} />
            <Route path="qualifications" element={<Qualifications />} />
            <Route path="job-preferences" element={<JobPreferences />} />
            <Route path="online-interview" element={<OnlineInterview />} />
            {/* End - Stage 1 */}
            {/* Start - Stage 2 */}
            <Route path="complaince-check" element={<ComplainceCheck />} />
            <Route path="complaince-check/edit" element={<ComplianceCheckEditPage />} />
            {/* End - Stage 2 */}
            {/* Start - Stage 3 */}
            <Route path="tfn-declaration" element={<TfnForm />} />
            <Route path="superannuation" element={<Superannuation />} />
            <Route path="contract-form" element={<ContractForm />} />
            <Route path="casual-and-fair-work-statement" element={<CasualAndFairWorkStatement />} />
            <Route path="bank-account" element={<BankAccount />} />
            <Route path="next-of-kin" element={<NextOfKin />} />
            {/* End - Stage 3 */}
          </Route>

          <Route
            path={AUTH_ROUTE.ADMIN_PENDING_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={ViewVacancy}
                roles={[Roles.SUPER_ADMIN, Roles.GENERAL_ADMIN]}
              />
            }
          />
          {/* End - Admin Routes */}

          {/* Start - Client Routes */}
          <Route
            path={AUTH_ROUTE.CLIENT_VACANCIES}
            element={
              <PrivateRoute
                layout={Portal}
                component={Vacancies}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.VACANCY_HISTORY}
            element={
              <PrivateRoute
                layout={Portal}
                component={VacanciesHistory}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CREATE_VACANCY}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateVacancy}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.EDIT_VACANCY}
            element={
              <PrivateRoute
                layout={Portal}
                component={CreateVacancy}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.PENDING_VACANCY}
            element={
              <PrivateRoute
                layout={Portal}
                component={ViewVacancy}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_CANDIDATES}
            element={
              <PrivateRoute
                layout={Portal}
                component={Candidates}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_CANDIDATES_BLOCKED}
            element={
              <PrivateRoute
                layout={Portal}
                component={BlockedCandidates}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_CENTRES}
            element={
              <PrivateRoute
                layout={Portal}
                component={ClientCenter}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_USERS}
            element={
              <PrivateRoute
                layout={Portal}
                component={CenterUsers}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_ENQUIRY}
            element={
              <PrivateRoute
                layout={Portal}
                component={CentreEnquiry}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_TIMESHEET_PENDING}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminTimesheet}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_TIMESHEET_APPROVAL}
            element={
              <PrivateRoute
                layout={Portal}
                component={ClientTimesheet}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_TIMESHEET_HISTORY}
            element={
              <PrivateRoute
                layout={Portal}
                component={AdminTimesheetHistory}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          <Route
            path={AUTH_ROUTE.CLIENT_SETTINGS}
            element={
              <PrivateRoute
                layout={Portal}
                component={ClientSettings}
                roles={[Roles.CENTER_ADMIN, Roles.CENTER_USER]}
              />
            }
          />
          {/* End - Client Routes */}
          <Route
            path="*"
            element={
              isAuthenticated() ? (
                <PrivateRoute
                  layout={Portal}
                  component={NotFoundLoggedIn}
                  roles={[
                    Roles.CANDIDATE,
                    Roles.CENTER_ADMIN,
                    Roles.CENTER_USER,
                    Roles.SUPER_ADMIN,
                    Roles.GENERAL_ADMIN,
                  ]}
                />
              ) : (
                <PublicRoute
                  layout={AuthLayout}
                  component={NotFoundLoggedOut}
                  extraProps={{ hideAuthLogo: true }}
                />
              )
            }
          />
        </Routes>
        <SaveNotification />
        <JobAdderId />
      </div>
    </>
  );
};

export default Router;
